import { Injectable } from '@angular/core';
// import { StrtPriority } from '../services/StrtPriority';
import { StrtPriority } from '../../shared/models/plan/StrtPriority';
import { SelectItem } from 'primeng/primeng';
import { IStrategyMapDownloadHeadings } from '../services/IStrategyMapDownloadHeadings';
import { DeliverableService } from '../services/deliverable.service';
import { BehaviorSubject } from 'rxjs';
// import { DataUtils } from '../services/data-utils';
import { DataUtils } from '../../shared/utilities/data-utils';
import { IWorkspace } from '../../models/IWorkspace';

export enum DownloadType {
  download,
  share
}

@Injectable()
export class StrategyMapDownloadService {
  private readonly DOWNLOAD_SUCCESS_HEADER = 'Your download is on the way.';
  private readonly DOWNLOAD_SUCCESS_MESSAGE = 'Once your download is ready, you will receive an email with a link to access the files.';
  private readonly SHARE_SUCCESS_HEADER = 'Share Successful';
  private readonly SHARE_SUCCESS_MESSAGE = 'Your output(s) is on the way.';
  private readonly ERROR_HEADER = 'There\'s been an error';

  private _isFormVisible: boolean;
  private _isRecipientsVisible: boolean;
  private _isConfirmVisible: boolean;
  private _initiativeTemplateItems: SelectItem[];
  private _workspaces: IWorkspace[];
  private _strategyId: number;
  private _strategyName: string;
  private _downloadType: DownloadType;
  private _confirmHeader: string;
  private _confirmMessage: string;
  private _strategyMapDownloadHeadings: IStrategyMapDownloadHeadings;
  private _resetForm = new BehaviorSubject<DownloadFormConfiguration>(null);

  resetForm$ = this._resetForm.asObservable();

  constructor(private deliverableService: DeliverableService) {}

  get strategyId(): number {
    return this._strategyId;
  }

  get strategyName(): string {
    return this._strategyName;
  }

  get isRecipientsVisible(): boolean {
    return this._isRecipientsVisible;
  }

  get isFormVisible(): boolean {
    return this._isFormVisible;
  }

  get isConfirmVisible(): boolean {
    return this._isConfirmVisible;
  }

  get initiativeTemplateItems(): SelectItem[] {
    return this._initiativeTemplateItems;
  }

  get workspaces(): IWorkspace[] {
    return this._workspaces;
  }

  get downloadType(): DownloadType {
    return this._downloadType;
  }

  get confirmHeader(): string {
    return this._confirmHeader;
  }

  get confirmMessage(): string {
    return this._confirmMessage;
  }

  setVisible(config: DownloadConfiguration): void {
    this._resetForm.next(config.formConfiguration);
    this._strategyId = config.strategyId;
    this._strategyName = config.strategyName;
    this._downloadType = config.downloadType;
    this._workspaces = config.workspaces;
    this._initiativeTemplateItems = DataUtils.reshapePriorities(config.priorities);

    // if the template items have more than one value, insert
    // a select all option at the top of the array
    if (this._initiativeTemplateItems.length > 1) {
      this._initiativeTemplateItems.unshift({ label: 'Select All', value: null });
    }

    this.showForm();
  }

  clearVisible(): void {
    this._isConfirmVisible = false;
    this._isRecipientsVisible = false;
    this._isFormVisible = false;
  }

  submitForm(headings: IStrategyMapDownloadHeadings): void {
    this._strategyMapDownloadHeadings = headings;

    if (this.downloadType === DownloadType.download) {
      // if it's a personal download we're done - submit it
      this.submitDownloadRequest(this.DOWNLOAD_SUCCESS_HEADER, this.DOWNLOAD_SUCCESS_MESSAGE);
    } else { // otherwise we need to get some more info
      this.showRecpients();
    }
  }

  submitShareForm(emails: string[], workspaceId: number) {
    this._strategyMapDownloadHeadings.emailRecipients = emails;
    this._strategyMapDownloadHeadings.workspaceId = workspaceId;
    this.submitDownloadRequest(this.SHARE_SUCCESS_HEADER, this.SHARE_SUCCESS_MESSAGE);
  }

  private submitDownloadRequest(successHeader: string, succesMessage: string): void {
    this.deliverableService.submitDownloadRequest(
      this._strategyId,
      this._strategyMapDownloadHeadings)
      .subscribe(
        () => {
          this._confirmHeader = successHeader;
          this._confirmMessage = succesMessage;
          this.showConfirm();
        },
        error => { this.showError(error); }
      );
  }

  private showForm(): void {
    this._resetVisibility();
    this._isFormVisible = true;
  }

  private showRecpients(): void {
    this._resetVisibility();
    this._isRecipientsVisible = true;
  }

  private showConfirm(): void {
    this._resetVisibility();
    this._isConfirmVisible = true;
  }

  private showError(error: Error): void {
    this._resetVisibility();
    this._confirmHeader = this.ERROR_HEADER;
    this._confirmMessage = error.message;
    this._isConfirmVisible = true;
  }

  private _resetVisibility(): void {
    this._isFormVisible = false;
    this._isRecipientsVisible = false;
    this._isConfirmVisible = false;
  }
}

export class DownloadFormConfiguration {
  allCheckbox = false;
  strategyMapCheckbox = false;
  annualPlansCheckbox = false;
  annualPlanStartDate: Date;
  annualPlanEndDate: Date;
  implPlanCheckbox = false;
  implPlanStartDate: Date;
  implPlanEndDate: Date;
  theoryOfActionCheckBox = false;
  submitButton: string;
  isPlanEmpty: boolean;
}

export class DownloadConfiguration {
  strategyId: number;
  strategyName: string;
  downloadType: DownloadType = DownloadType.download;
  priorities: StrtPriority[];
  workspaces: IWorkspace[];
  formConfiguration: DownloadFormConfiguration = new DownloadFormConfiguration();
}




